import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { type ReactNode } from 'react';
import { AuthProvider, useAuth } from '@/lib/auth';
import { ScrollRestoration } from '@/lib/scroll-restoration';
import { AppLayout } from './layout';
import { LoginPage } from '@/pages/login';
import { InviteAcceptPage } from '@/pages/invite-accept';
import { PasswordResetPage } from '@/pages/password-reset';
import { DashboardPage } from '@/pages/dashboard';
import { CompaniesPage } from '@/pages/companies';
import { CompanyDetailPage } from '@/pages/company-detail';
import { CampaignsPage } from '@/pages/campaigns';
import { ListsPage } from '@/pages/lists';
import { ListDetailPage } from '@/pages/list-detail';
import { CampaignNewPage } from '@/pages/campaign-new';
import { CampaignDetailPage } from '@/pages/campaign-detail';
import { CampaignPreviewPage } from '@/pages/campaign-preview';
import { MessageEditPage } from '@/pages/message-edit';
import { InboxPage } from '@/pages/inbox';
import { InboxDetailPage } from '@/pages/inbox-detail';
import { MailPage } from '@/pages/mail';
import { MailThreadPage } from '@/pages/mail-thread';
import { AppointmentsPage } from '@/pages/appointments';
import { ActivitiesPage } from '@/pages/activities';
import { AnalyticsPage } from '@/pages/analytics';
import { SettingsPage } from '@/pages/settings';
import { SettingsProductsPage } from '@/pages/settings-products';
import { SettingsUsersPage } from '@/pages/settings-users';
import { SettingsSuppressionsPage } from '@/pages/settings-suppressions';
import { SettingsTagsPage } from '@/pages/settings-tags';
import { SettingsNewsletterPage } from '@/pages/settings-newsletter';
import { SettingsIntegrationsPage } from '@/pages/settings-integrations';
import { SettingsMailPage } from '@/pages/settings-mail';
import { SettingsMailReceivePage } from '@/pages/settings-mail-receive';
import { SettingsGoogleWorkspacePage } from '@/pages/settings-google-workspace';
import { SettingsTemplatesPage } from '@/pages/settings-templates';
import { SettingsLimitsPage } from '@/pages/settings-limits';
import { LogsPage } from '@/pages/logs';
import { UsagePage } from '@/pages/usage';
import { JobsPage } from '@/pages/jobs';
import { NotFoundPage } from '@/pages/not-found';
import { messages } from '@nesc/shared';

/** 未認証なら S01 へ。直前に開こうとした画面は復帰用に保持する */
const RequireAuth = ({ children }: { children: ReactNode }) => {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <p className="text-body text-foreground-muted">{messages.common.loading}</p>
      </div>
    );
  }
  if (status === 'anonymous') {
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }
  return <>{children}</>;
};

export const App = () => (
  <BrowserRouter>
    <AuthProvider>
      <ScrollRestoration />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/invite/:token" element={<InviteAcceptPage />} />
        <Route path="/password-reset" element={<PasswordResetPage />} />

        <Route
          element={
            <RequireAuth>
              <AppLayout />
            </RequireAuth>
          }
        >
          <Route path="/" element={<DashboardPage />} />
          <Route path="/companies" element={<CompaniesPage />} />
          <Route path="/companies/:id" element={<CompanyDetailPage />} />
          <Route path="/lists" element={<ListsPage />} />
          <Route path="/lists/:id" element={<ListDetailPage />} />
          <Route path="/campaigns" element={<CampaignsPage />} />
          <Route path="/campaigns/new" element={<CampaignNewPage />} />
          <Route path="/campaigns/:id" element={<CampaignDetailPage />} />
          <Route path="/campaigns/:id/preview" element={<CampaignPreviewPage />} />
          <Route path="/messages/:id" element={<MessageEditPage />} />
          <Route path="/inbox" element={<InboxPage />} />
          <Route path="/inbox/:id" element={<InboxDetailPage />} />
          <Route path="/mail" element={<MailPage />} />
          <Route path="/mail/:threadId" element={<MailThreadPage />} />
          <Route path="/newsletter" element={<SettingsNewsletterPage />} />
          <Route path="/appointments" element={<AppointmentsPage />} />
          <Route path="/activities" element={<ActivitiesPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/products" element={<SettingsProductsPage />} />
          <Route path="/settings/users" element={<SettingsUsersPage />} />
          <Route path="/settings/suppressions" element={<SettingsSuppressionsPage />} />
          <Route path="/settings/tags" element={<SettingsTagsPage />} />
          <Route path="/settings/newsletter" element={<Navigate to="/newsletter" replace />} />
          <Route path="/settings/integrations" element={<SettingsIntegrationsPage />} />
          <Route path="/settings/mail" element={<SettingsMailPage />} />
          <Route path="/settings/mail-receive" element={<SettingsMailReceivePage />} />
          <Route path="/settings/google-workspace" element={<SettingsGoogleWorkspacePage />} />
          <Route path="/settings/templates" element={<SettingsTemplatesPage />} />
          <Route path="/settings/limits" element={<SettingsLimitsPage />} />
          <Route path="/logs" element={<LogsPage />} />
          <Route path="/usage" element={<UsagePage />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </AuthProvider>
  </BrowserRouter>
);
